import type { ConstValues } from '@openx/types/helpers.ts';
import type { BulkValidationReturnType } from './bulk/types.ts';

export const ActionType = {
  Block: 'BLOCK',
  Add: 'ADD',
} as const;
export type ActionType = ConstValues<typeof ActionType>;

export type AsyncSearchUnionProps = WithAsyncSearchProps | WithoutAsyncSearchProps;

export type WithAsyncSearchProps = {
  isAsyncSearch: true;
  asyncSearchFn: (input: string) => Promise<void>;
};

export type WithoutAsyncSearchProps = {
  isAsyncSearch?: false;
};

export type HeaderListUnionProps<T = unknown> = WithHeaderListProps | WithoutHeaderListProps<T>;

type WithoutHeaderListProps<T = unknown> = {
  isHeaderList: false;
  handleRowClick: ((id: string) => void) | ((item: T) => void);
};

type WithHeaderListProps = {
  isHeaderList: true;
  parentId: string;
  handleRowClick: (id: string, parentId: string) => void;
};

export type SelectedListUnionProps = WithSelectedListProps | WithoutSelectedListProps;

export type WithSelectedListProps = {
  isSelectedList: true;
  handleRemoveAll: () => void;
  actionType: ActionType;
};

export type WithoutSelectedListProps = {
  isSelectedList: false;
};

export type BulkInputUnionProps = WithBulkInputProps | WithoutBulkInputProps;

export type WithBulkInputProps = {
  hasBulkInput: true;
  handleBulkValidation: (items: string[]) => BulkValidationReturnType;
  handleBulkAdd: (items: string[]) => void;
};

export type WithoutBulkInputProps = {
  hasBulkInput: false;
};

export type HeaderSelectedListUnionProps =
  | Pick<WithSelectedListProps, 'isSelectedList' | 'handleRemoveAll'>
  | WithoutSelectedListProps;

export type ItemUnionTypeProps =
  | Pick<WithSelectedListProps, 'isSelectedList' | 'actionType'>
  | WithoutSelectedListProps;

export type VirtualiedListUnionTypeProps =
  | Pick<WithSelectedListProps, 'isSelectedList' | 'actionType'>
  | WithoutSelectedListProps;

export type BaseListOption = {
  id: string;
  name: string;
};

export type EnrichedListOption<T> = {
  isOnList: boolean;
  isFrozen?: boolean;
} & T;

export type EnrichedHeaderListChildOption<T> = EnrichedListOption<T> & { parentId: string };

export type HeaderListOption<T> = {
  isHeaderOnList?: boolean;
  nodes: EnrichedHeaderListChildOption<T>[];
  nodeCount: number;
} & BaseListOption;

export type HeaderOptionMap<T> = {
  isHeaderOnList?: boolean;
  nodes: Record<string, EnrichedHeaderListChildOption<T>>;
} & BaseListOption;

export type CustomItemRenderProps<T> = {
  item: T;
  style: React.CSSProperties;
  isDisabled: boolean;
  searchInput: string;
  handleRowClick: (item: T) => void;
  hideActionIcon?: boolean;
};

export type CustomItemRender<T> = (props: CustomItemRenderProps<T>) => React.ReactNode;
